import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { RfqSupplierInvitation } from '@/types/domain/rfq-management';
import { formatCurrency } from '@/lib/utils';
import { AlertTriangle, RefreshCw, Users, Send } from 'lucide-react';

interface RfqSupplierPanelProps {
  suppliers: RfqSupplierInvitation[];
  onOpenInviteModal?: () => void;
  onRetryFailedInvitations?: () => void;
  isRetrying?: boolean;
}

export const RfqSupplierPanel: React.FC<RfqSupplierPanelProps> = ({
  suppliers,
  onOpenInviteModal,
  onRetryFailedInvitations,
  isRetrying = false,
}) => {
  const failedInvitations = suppliers.filter((s) => s.invitationStatus === 'FAILED');

  return (
    <Card className="border-border rounded-2xl shadow-2xs font-heading">
      <CardHeader className="pb-3 border-b border-border/60 flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" /> Invited Enterprise Suppliers & Quotation Bids ({suppliers.length})
        </CardTitle>

        {onOpenInviteModal && (
          <Button size="sm" onClick={onOpenInviteModal} className="rounded-xl font-bold bg-primary text-white">
            <Send className="h-3.5 w-3.5 mr-1.5" /> + Invite Suppliers
          </Button>
        )}
      </CardHeader>

      <CardContent className="pt-4 space-y-4">
        {/* Partial Invitation Failure Callout */}
        {failedInvitations.length > 0 && (
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-center justify-between text-xs font-bold text-rose-700 dark:text-rose-300">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                Partial Invitation Failure: {failedInvitations.length} invitation(s) failed delivery via SMTP Gateway.
              </span>
            </div>

            {onRetryFailedInvitations && (
              <Button
                size="sm"
                disabled={isRetrying}
                onClick={onRetryFailedInvitations}
                className="rounded-xl font-bold bg-rose-600 hover:bg-rose-700 text-white shrink-0"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRetrying ? 'animate-spin' : ''}`} />
                {isRetrying ? 'Retrying Transmit...' : 'Retry Transmit'}
              </Button>
            )}
          </div>
        )}

        {/* Supplier Invitation List */}
        <div className="space-y-3">
          {suppliers.map((sup) => (
            <div
              key={sup.id}
              className="p-4 rounded-xl border border-border/80 bg-card flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-extrabold text-foreground text-sm">{sup.supplierName}</span>
                  <span className="font-mono text-[10px] text-foreground-muted">ID: {sup.supplierId}</span>
                </div>
                <p className="text-foreground-secondary font-medium text-[11px]">
                  Category: <strong>{sup.category}</strong> • Contact: <span className="font-mono">{sup.contactEmail}</span>
                </p>
              </div>

              <div className="flex items-center gap-4">
                {sup.quotationId && sup.quotedAmount ? (
                  <div className="text-right">
                    <span className="font-mono font-bold text-purple-600 text-xs block">Ref: {sup.quotationId}</span>
                    <span className="font-extrabold text-foreground text-sm block">
                      {formatCurrency(sup.quotedAmount, sup.currency || 'INR')}
                    </span>
                  </div>
                ) : (
                  <span className="text-foreground-muted font-bold text-xs italic">Awaiting Quotation Bid</span>
                )}

                <span
                  className={`text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-full ${
                    sup.invitationStatus === 'RESPONDED'
                      ? 'bg-purple-500/10 text-purple-700 border border-purple-500/20'
                      : sup.invitationStatus === 'FAILED'
                      ? 'bg-rose-500/10 text-rose-700 border border-rose-500/20'
                      : 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/20'
                  }`}
                >
                  {sup.invitationStatus}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
